import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Icon, Text } from '@feathr/components';
import { getImportFailureMessage } from '@feathr/extender/utils/importFailureMessage';
import { cssVar } from '@feathr/hooks';

interface IFailureReasonLineProps {
  importer: Importer;
  showIcon?: boolean;
}

/**
 * The API's sentence for why a failed import failed, or the generic sentence when it stored none,
 * after a warning icon unless `showIcon` is false. Nothing for any other state.
 */
function FailureReasonLine({
  importer,
  showIcon = true,
}: Readonly<IFailureReasonLineProps>): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (importer.get('state') !== 'failed') {
    return null;
  }

  const sentence = getImportFailureMessage(importer.get('failure_message'), t);

  return (
    <Flex align={'center'} gap={theme.spacing['1']}>
      {showIcon && <Icon color={cssVar('--color-warning')} icon={faExclamationTriangle} />}
      <Text.DefaultRegular truncate={false}>{sentence}</Text.DefaultRegular>
    </Flex>
  );
}

export default observer(FailureReasonLine);
